import { useEffect } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";
import { useNavigate } from 'react-router-dom';
import { getUserAccessLevel } from 'utils/setup';

import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';
import { setCameraDeviceIcon } from 'utils/map';
import { setMapIcons } from 'utils/map';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

const BATTERY_THRESHOLD = 25;

function setDevicePointColour(device, user_type) {
    if (!device.active && getUserAccessLevel(user_type, 'technician')) {
        return { feature_colour: '#555555', zIndex: 4 };
    }
    else {
        if (device.connected_state == 'sleeping') {
            return { feature_colour: '#555555', zIndex: 4 };
        }
        else if (device.connected_state != "online") {
            return { feature_colour: "#ff667c", zIndex: 3 };
        }
        else if (device.battery <= BATTERY_THRESHOLD && getUserAccessLevel(user_type, 'technician')) {
            return { feature_colour: "#edaa00", zIndex: 2 };
        }
        else {
            return { feature_colour: "#58ba2d", zIndex: 1 };
        }
    }
}

function setDeviceFeatures(device, user_type) {
    var ret = {
        type: "Feature",
        properties: {
            ...device,
            access: getUserAccessLevel(user_type, 'technician'),
            ...setDevicePointColour(device, user_type)
        },
        geometry: {
            type: 'Point',
            coordinates: device.position
        }
    }

    if (device.hardware_type == "bk-camera") {
        ret.properties.icon = setCameraDeviceIcon(device, user_type);
    }

    return ret;
}

function StopmonitorMap({ map, user, devices, selectedDevice, setSelectedDevice }) {

    const navigate = useNavigate();

    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
    });

    const handleDeviceClick = (e) => {
        e.preventDefault();
        popup.remove();
        navigate("/devices/" + e.features[0].properties.uuid);
    }

    const handleMapClick = (e) => {
        if (!e.defaultPrevented) {
            setSelectedDevice(null);
            // navigate("/overview");
        }
    }

    const handleMouseEnter = (e) => {

        map.current.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();
        var description = `<div style='text-align:center; font-size:13px'><b>${e.features[0].properties.name}</b></div>`;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);

        const parsedProps = { ...e.features[0].properties };
        for (let key in parsedProps) {
            try {
                if (typeof parsedProps[key] === 'string' && (parsedProps[key].startsWith('{') || parsedProps[key].startsWith('['))) {
                    parsedProps[key] = JSON.parse(parsedProps[key]);
                }
            } catch (err) { }
        }
        setSelectedDevice(parsedProps)
    }

    const handleMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
        // setSelectedDevice(null);
        popup.remove();
    }

    const setupLayers = async () => {
        if (!map.current) return;

        const icons = [
            { id: 'cameraIconOnline', img: cameraOnline },
            { id: 'cameraIconWarning', img: cameraWarning },
            { id: 'cameraIconOffline', img: cameraOffline }
        ];

        // 1. Load all images and wait for them to finish
        await Promise.all(icons.map(icon => {
            return new Promise((resolve) => {
                if (map.current.hasImage(icon.id)) {
                    resolve();
                } else {
                    map.current.loadImage(icon.img, (err, res) => {
                        if (!err && res && !map.current.hasImage(icon.id)) {
                            map.current.addImage(icon.id, res);
                        }
                        resolve();
                    });
                }
            });
        }));

        // 2. NOW add the sources and layers
        // Camera Layer
        if (!map.current.getSource('devices-img-source')) {
            map.current.addSource('devices-img-source', {
                type: 'geojson',
                data: {
                    type: "FeatureCollection",
                    features: devices.filter(d => d.hardware_type === "bk-camera")
                        .map(d => setDeviceFeatures(d, user.user.user_type))
                }
            });

            map.current.addLayer({
                id: 'devices-img-layer',
                type: 'symbol',
                source: 'devices-img-source',
                layout: {
                    'icon-image': ['get', 'icon'],
                    'icon-size': 0.044,
                    'icon-allow-overlap': true // Ensures they show up even if crowded
                }
            });
        }

        // Standard Circle Layer
        if (!map.current.getSource('devices-source')) {
            map.current.addSource('devices-source', {
                type: 'geojson',
                data: {
                    type: "FeatureCollection",
                    features: devices.filter(d => d.hardware_type !== "bk-camera")
                        .map(d => setDeviceFeatures(d, user.user.user_type))
                }
            });

            map.current.addLayer({
                id: 'devices-layer',
                type: 'circle',
                source: 'devices-source',
                paint: {
                    'circle-color': ['get', 'feature_colour'],
                    'circle-radius': 9,
                    'circle-stroke-width': 3,
                    'circle-stroke-color': '#ffffff'
                }
            });
        }

        const isDark = user.user.mode === "dark";
        map.current.setFog(isDark ? {
            'color': 'rgba(255,255,255,0.06)',
            'high-color': 'rgba(170,190,255,0.25)',
            'space-color': '#05070f',
            'horizon-blend': 0.18,
            'star-intensity': 0.25
        } : {
            'color': '#ffffff',
            'high-color': '#ffffff',
            'space-color': '#333333',
            'horizon-blend': 0.05,
            'star-intensity': 0.4
        });
    };

    useEffect(() => {
        if (!map.current) return;

        // If the style is already loaded, setup. Otherwise wait for it.
        if (map.current.isStyleLoaded()) {
            setupLayers();
        }

        const handleStyleLoad = () => setupLayers();
        map.current.on('style.load', handleStyleLoad);

        // Click handlers (only need to be attached once per map instance)
        map.current.on('click', 'devices-img-layer', handleDeviceClick);
        map.current.on('mouseenter', 'devices-img-layer', handleMouseEnter);
        map.current.on('mouseleave', 'devices-img-layer', handleMouseLeave);
        map.current.on('click', 'devices-layer', handleDeviceClick);
        map.current.on('mouseenter', 'devices-layer', handleMouseEnter);
        map.current.on('mouseleave', 'devices-layer', handleMouseLeave);
        map.current.on('click', handleMapClick);

        return () => {
            map.current.off('style.load', handleStyleLoad);
            // Optional: clean up layers if this component unmounts
            if (map.current.getLayer('devices-layer')) {
                map.current.removeLayer('devices-layer');
                map.current.removeSource('devices-source');
            }
            if (map.current.getLayer('devices-img-layer')) {
                map.current.removeLayer('devices-img-layer');
                map.current.removeSource('devices-img-source');
            }
        };
    }, [user?.user?.mode]); // Fires every time mode changes
}

StopmonitorMap.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    devices: PropTypes.array,
    selectedDevice: PropTypes.object,
    setSelectedDevice: PropTypes.func,
};

export default StopmonitorMap;